import { useState } from 'react';
import {
  IonAlert,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonNote,
} from '@ionic/react';
import { carSportOutline, createOutline, peopleOutline, trashOutline, locationOutline } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { listPlaces, renamePlace, deletePlace } from '../lib/places';
import { deleteSavedRide, listSavedRides, renameSavedRide } from '../lib/savedrides';
import type { SavedPlace, SavedRide } from '../lib/types';

export function Places({ onBack }: { onBack: () => void }) {
  const [places, setPlaces] = useState<SavedPlace[]>(() => listPlaces());
  const [rides, setRides] = useState<SavedRide[]>(() => listSavedRides());
  const [renamingPlace, setRenamingPlace] = useState<SavedPlace | null>(null);
  const [renamingRide, setRenamingRide] = useState<SavedRide | null>(null);

  return (
    <Page title="Meine Orte" onBack={onBack}>
      {rides.length > 0 && (
        <Card flush>
          <div style={{ padding: '18px 18px 4px' }}>
            <h2 style={{ margin: 0 }}>Gespeicherte Fahrten</h2>
          </div>
          <IonList lines="full">
            {rides.map((ride) => (
              <IonItemSliding key={ride.id}>
                <IonItem>
                  <IonIcon
                    slot="start"
                    icon={ride.contactId ? peopleOutline : carSportOutline}
                    color="primary"
                  />
                  <IonLabel>
                    <h3>{ride.name}</h3>
                    <IonNote>
                      {ride.contactLabel
                        ? `mit ${ride.contactLabel}`
                        : ride.pickup
                          ? 'Startpunkt und Abholpunkt gespeichert'
                          : 'nur Startpunkt gespeichert'}
                    </IonNote>
                  </IonLabel>
                </IonItem>
                <IonItemOptions side="end">
                  <IonItemOption onClick={() => setRenamingRide(ride)}>
                    <IonIcon slot="icon-only" icon={createOutline} />
                  </IonItemOption>
                  <IonItemOption color="danger" onClick={() => setRides(deleteSavedRide(ride.id))}>
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </IonList>
        </Card>
      )}

      {places.length === 0 ? (
        <Card>
          <h2>Noch nichts gespeichert</h2>
          <p>
            Beim Setzen eines Start- oder Abholpunktes kannst du ihn mit "Ort speichern" unter
            einem eigenen Namen ablegen. Danach steht er hier und in der Schnellauswahl.
          </p>
        </Card>
      ) : (
        <Card flush>
          <IonList lines="full">
            {places.map((place) => (
              <IonItemSliding key={place.id}>
                <IonItem>
                  <IonIcon slot="start" icon={locationOutline} color="primary" />
                  <IonLabel>
                    <h3>{place.name}</h3>
                    <IonNote>
                      {place.lat.toFixed(5)}, {place.lng.toFixed(5)}
                    </IonNote>
                  </IonLabel>
                </IonItem>
                <IonItemOptions side="end">
                  <IonItemOption onClick={() => setRenamingPlace(place)}>
                    <IonIcon slot="icon-only" icon={createOutline} />
                  </IonItemOption>
                  <IonItemOption color="danger" onClick={() => setPlaces(deletePlace(place.id))}>
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </IonList>
        </Card>
      )}

      <p className="r2f-muted">
        Gespeicherte Orte und Fahrten bleiben nur auf diesem Gerät - sie werden nie an den
        Server übertragen.
      </p>

      <IonAlert
        isOpen={renamingPlace !== null}
        header="Ort umbenennen"
        inputs={[
          {
            name: 'name',
            type: 'text',
            value: renamingPlace?.name ?? '',
            attributes: { maxlength: 40 },
          },
        ]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          {
            text: 'Speichern',
            handler: (data: { name?: string }) => {
              if (renamingPlace && data.name?.trim()) setPlaces(renamePlace(renamingPlace.id, data.name));
            },
          },
        ]}
        onDidDismiss={() => setRenamingPlace(null)}
      />

      <IonAlert
        isOpen={renamingRide !== null}
        header="Fahrt umbenennen"
        inputs={[
          {
            name: 'name',
            type: 'text',
            value: renamingRide?.name ?? '',
            attributes: { maxlength: 40 },
          },
        ]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          {
            text: 'Speichern',
            handler: (data: { name?: string }) => {
              if (renamingRide) setRides(renameSavedRide(renamingRide.id, data.name ?? ''));
            },
          },
        ]}
        onDidDismiss={() => setRenamingRide(null)}
      />
    </Page>
  );
}
